import { useRef, useState } from 'react';
import {
  SignatureModel,
  SendAsModel,
  User,
  CustomSchemaEntity,
} from '../../../../../../types';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  UserKeys,
  addToGlobalBlockList,
  getSignatureWithValues,
  removeFromGlobalBlockList,
  updateGmailSignature,
} from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';

import AllFields from './all-fields';
import ChangeLogDialog from '../../../../../shared/change-log-dialog';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import TextEditor from '../../../../../shared/editor/text-editor';
import { Editor } from 'tinymce';

import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import HtmlPreview from '../../../../../shared/html-preview';
import { Messages } from '../../../../../utils/message-constants';

import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardHeader from '@mui/material/CardHeader';

interface SignaturesFormProps {
  userEmail: string;
  userRoles: string[];
  sendAsAccounts: SendAsModel[];
  signatures: SignatureModel[];
  isUserExcluded: boolean;
  user: User;
  customSchemas: CustomSchemaEntity[];
  changeLogRoles: string[];
}

export default function SignaturesForm({
  userEmail,
  userRoles,
  sendAsAccounts,
  signatures,
  isUserExcluded,
  user,
  customSchemas,
  changeLogRoles,
}: SignaturesFormProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const sortedAccounts = [
    ...sendAsAccounts.sort((a, b) =>
      a.sendAsEmail.localeCompare(b.sendAsEmail)
    ),
  ];

  const [currentSendAsEmail, setCurrentSendAsEmail] = useState(
    sortedAccounts.find((account) => account.isPrimary)?.sendAsEmail || ''
  );

  const currentSendAsAccount = sortedAccounts.find(
    (account) => account.sendAsEmail === currentSendAsEmail
  );

  const [currentSignature, setCurrentSignature] = useState(
    sortedAccounts.find((account) => account.isPrimary)?.signature || ''
  );

  const editorRef = useRef<Editor | null>(null);

  const { data } = useQuery({
    queryKey: [
      UserKeys.SignatureValue,
      userEmail,
      currentSendAsAccount?.sendAsEmail,
      currentSignature,
    ],
    queryFn: () =>
      getSignatureWithValues({
        template: currentSignature,
        user,
        sendAs: currentSendAsAccount!,
        requestTime: new Date().getTime(),
      }),
    enabled: !!currentSendAsAccount,
    placeholderData: keepPreviousData,
  });

  const { mutate, isPending: isUpdating } = useMutation({
    mutationFn: () =>
      updateGmailSignature(
        userEmail,
        currentSendAsEmail,
        data?.populatedTemplate ||
          sortedAccounts.find((account) => account.isPrimary)?.signature ||
          '',
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Signature'),
      });
      const currentData = queryClient.getQueryData([
        UserKeys.SendAsAccounts,
        userEmail,
      ]) as SendAsModel[];
      const updatedData = currentData.map((account) => {
        if (account.sendAsEmail === currentSendAsEmail) {
          return {
            ...account,
            signature: currentSignature,
          };
        } else {
          return account;
        }
      });
      queryClient.setQueryData(
        [UserKeys.SendAsAccounts, userEmail],
        updatedData
      );
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          Messages.Errors.Network.Updating(`the user's signature`),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings.update_signature,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.signatures,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: excludeUserMutate } = useMutation({
    mutationFn: () => addToGlobalBlockList(userEmail),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'User added to the exclude list successfully!',
      });
      queryClient.setQueryData(
        [UserKeys.CheckIfInGlobalBlockList, userEmail],
        !isUserExcluded
      );
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Adding(
          'this user to the exclude list of signature template runs.'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .update_exclude_list,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.signatures,
        }
      );
    },
  });

  const { mutate: nonExcludeUserMutate } = useMutation({
    mutationFn: () => removeFromGlobalBlockList(userEmail),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'User removed from the exclude list successfully!',
      });
      queryClient.setQueryData(
        [UserKeys.CheckIfInGlobalBlockList, userEmail],
        !isUserExcluded
      );
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting(
          'this user from the exclude list of signature template runs.'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .update_exclude_list,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.signatures,
        }
      );
    },
  });

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        action={
          <Button
            variant="contained"
            onClick={() => {
              if (
                isAbleToSeeUIElement(
                  changeLogRoles,
                  UserRoles.SetGmailSignature.value
                )
              ) {
                setOpenChangeLogDialog(true);
              } else {
                mutate();
              }
            }}
            disabled={
              isUpdating ||
              !isAbleToSeeUIElement(
                userRoles,
                UserRoles.SetGmailSignature.value
              )
            }
            loading={isUpdating}
            color="primary"
            type="submit"
          >
            Save
          </Button>
        }
      />
      <CardContent
        sx={{
          px: 4,
          py: 0,
          flexGrow: 1,
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        <Stack gap={3}>
          <FormControlLabel
            sx={{ width: 'fit-content' }}
            control={
              <Checkbox
                onChange={() => {
                  if (isUserExcluded) {
                    nonExcludeUserMutate();
                  } else {
                    excludeUserMutate();
                  }
                }}
                checked={isUserExcluded}
              />
            }
            label="Exclude this user from Signature Template runs"
          />
          <Stack flexDirection="row" alignItems="center" gap={2}>
            <TextField
              select
              sx={{ width: 300 }}
              value={currentSendAsEmail}
              label="Send As Account"
              onChange={(event) => {
                setCurrentSendAsEmail(event.target.value);
                const selectedAccount = sortedAccounts.find(
                  (account) => account.sendAsEmail === event.target.value
                );
                setCurrentSignature(selectedAccount!.signature || '');
              }}
            >
              {sortedAccounts.map((option) => (
                <MenuItem value={option.sendAsEmail} key={option.sendAsEmail}>
                  {option.sendAsEmail}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack flexDirection="row" alignItems="center" gap={9}>
            <TextField
              select
              label="Template Options"
              onChange={(event) => {
                if (event.target.value === 'currentSignature') {
                  const selectedAccount = sortedAccounts.find(
                    (account) => account.sendAsEmail === currentSendAsEmail
                  );
                  setCurrentSignature(selectedAccount!.signature || '');
                } else {
                  setCurrentSignature(event.target.value);
                }
              }}
              sx={{ width: 300 }}
              defaultValue={'currentSignature'}
            >
              {
                <MenuItem value={'currentSignature'} key={'currentSignature'}>
                  {'Current Signature'}
                </MenuItem>
              }
              {signatures.map((option) => (
                <MenuItem value={option.templateHtml} key={option.templateHtml}>
                  {option.templateName}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Divider />
          <AllFields
            user={user}
            customSchemas={customSchemas}
            editorRef={editorRef}
          />
          <Stack gap={2}>
            <Box sx={{ height: 500 }}>
              <TextEditor
                editorRef={editorRef}
                content={currentSignature}
                setContent={setCurrentSignature}
              />
            </Box>
            {editorRef.current ? (
              <HtmlPreview html={data?.populatedTemplate || ''} />
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={mutate}
        mutateLoading={isUpdating}
      />
    </Card>
  );
}
